import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdArrowBack, MdClose } from 'react-icons/md';
import { HiExternalLink } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import blogs from '../../data/blogs.json';
import './AllBlogs.scss';

const AllBlogs = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="ab__page">
      {/* Header */}
      <div className="ab__header">
        <button className="ab__back-btn" onClick={() => navigate('/')}>
          <MdArrowBack />
          <span>Home</span>
        </button>
        <div className="ab__header-text">
          <h1 className="ab__title">All Articles</h1>
          <p className="ab__subtitle">My writings on engineering, AI, and building things</p>
        </div>
      </div>

      {/* Grid */}
      <div className="ab__grid">
        {blogs.map((blog, index) => (
          <motion.div
            key={blog.title || index}
            className="ab__card app__flex"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.06 }}
            onClick={() => setSelectedBlog(blog)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedBlog(blog)}
          >
            {/* Image */}
            <div className="ab__card-img app__flex">
              <img src={blog.coverUrl} alt={blog.title} loading="lazy" />
              <motion.div
                whileHover={{ opacity: [0, 1] }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="ab__card-hover app__flex"
              >
                <div className="app__flex"><HiExternalLink /></div>
              </motion.div>
            </div>

            {/* Content */}
            <div className="ab__card-content app__flex">
              <div className="ab__card-tag app__flex">
                <p className="p-text">{blog.tags[0]}</p>
              </div>
              <h3>{blog.title}</h3>
              <div className="ab__card-data">
                <p className="p-text">{blog.summary}</p>
                <div className="ab__card-bubbles">
                  {blog.tags.map((tag, i) => (
                    <span key={i} className="ab__card-bubble">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal / preview panel */}
      <AnimatePresence>
        {selectedBlog && (
          <motion.div
            className="ab__modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedBlog(null)}
          >
            <motion.div
              className="ab__modal"
              initial={{ opacity: 0, scale: 0.92, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 40 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="ab__modal-close" onClick={() => setSelectedBlog(null)}>
                <MdClose />
              </button>

              <div className="ab__modal-hero-wrap">
                <img src={selectedBlog.coverUrl} alt={selectedBlog.title} className="ab__modal-hero" />
              </div>

              <div className="ab__modal-body">
                <div className="ab__card-tags" style={{ marginBottom: '0.75rem' }}>
                  {selectedBlog.tags.map((tag, i) => (
                    <span key={i} className="ab__tag">{tag}</span>
                  ))}
                </div>
                <h2 className="ab__modal-title">{selectedBlog.title}</h2>
                <p className="ab__modal-summary">{selectedBlog.summary}</p>
                <span className="ab__modal-date">{selectedBlog.date}</span>

                <a
                  href={selectedBlog.mediumUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="ab__modal-read-btn"
                >
                  <HiExternalLink size={18} />
                  Read Full Article on Medium
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AllBlogs;
